import type { SearchParams } from "@/modules/jobAds/client/api/types"

import { JOB_ADS_CANDIDACY_TYPES } from "@/modules/jobAds/client/api/constants"
import {
  DEFAULT_RADIUS_KM,
  DEFAULT_RESULTS_SORT_OPTION,
  PUBLICATION_DATE_FILTER_OPTIONS,
} from "@/modules/jobAds/client/constants"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { getJobAdsSearchParamsFromURLSearchParams } from "@/modules/jobAds/client/utils/getJobAdsSearchParamsFromURLSearchParams"
import { usePageContext } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { useLocale } from "@/modules/locales/useLocale"
import { computeLocationObjectFromSingleQueryParam, getLocationType } from "@/modules/locations/locations"
import { useTracking } from "@/modules/tracking/useTracking"

export type JobAdDisplayNextEventProperties = {
  company_id: string
  job_ad_id: string
  position: number
  sponsored_job_ads: boolean
  index: string
}

type JobAdSaveEventProperties = {
  job_ad_id: string
  context?: string
  index: string
}

type JobAdClickEventProperties = {
  job_ad_id: string
  position: number
  is_public: boolean
  easy_to_apply: boolean
  context?: string
  index: string
}

type AutocompleteSearchEventProperties = {
  keyword_searched: string
  suggestion_clicked: string
  rank_suggestion_clicked: number
}

export type JobAdsSearchEventProperties = {
  autocompleteSearch?: AutocompleteSearchEventProperties
  algoliaSearchId?: string
  nbResults: number
}

type SavedSearchModuleProperties = "fo_dashboard" | "job_ads_fo"

type SavedSearchFormStep = "new" | "update"

type UseJobAdsClientTracking = {
  setRefererParams: (data: unknown, options?: { keyName: string }) => void
  trackJobAdClick: (properties: JobAdClickEventProperties) => void
  trackJobAdDisplayNext: (properties: JobAdDisplayNextEventProperties) => void
  trackJobAdSave: (like: boolean, properties: JobAdSaveEventProperties) => void
  trackJobAdsSearch: (properties?: JobAdsSearchEventProperties) => void
  trackSavedSearchClick: (searchParams: SearchParams, savedSearchId: string) => void
  trackSavedSearchCreated: (searchParams: SearchParams, options?: { hasAlreadySavedSearch?: boolean }) => void
  trackSavedSearchDeleted: (searchParams: SearchParams, module: SavedSearchModuleProperties) => void
  trackSavedSearchJobAlertOptInClick: (isOptIn: boolean, module: SavedSearchModuleProperties) => void
  trackSavedSearchSubmitForm: (step: SavedSearchFormStep) => void
}

export const useJobAdsClientTracking = (): UseJobAdsClientTracking => {
  const tracking = useTracking()
  const {
    isPublicationDateFilterEnabled,
    trackingContext: { careerCenterId, exhaustiveFacets, exhaustiveResults, pageSize, searchId, totalSize, userId },
  } = usePageContext()
  const locale = useLocale()
  const searchParams = useJobAdsSearchParams()

  const {
    candidacyTypes,
    companyBusinessTypes,
    companySectors,
    contractDurations,
    contractTypes,
    exclusive,
    isSavedSearch,
    jobWithImpact,
    location,
    page: pageNumber,
    positionCategories,
    publicationDate,
    radius,
    remoteTypes,
    sort,
    startDates,
    studyLevels,
    tags,
    userQuery,
    workExperiences,
  } = getJobAdsSearchParamsFromURLSearchParams(searchParams)
  // The `page` variable is 0-indexed while we want the human readable value
  const page = pageNumber + 1

  const sortBy = sort || DEFAULT_RESULTS_SORT_OPTION

  const setRefererParams = (data: unknown, options?: { keyName: string }): void =>
    tracking?.setRefererParams(data, options || {})

  const getRadiusDefaultValue = (initialRadius: number | null, initialLocation: string | null): number | undefined => {
    if (initialRadius) {
      return initialRadius
    }

    const isLocationCity =
      initialLocation && getLocationType(computeLocationObjectFromSingleQueryParam(initialLocation)) === "city"

    if (isLocationCity) {
      return DEFAULT_RADIUS_KM
    }

    return undefined
  }

  const trackJobAdDisplayNext = (properties: JobAdDisplayNextEventProperties): void =>
    tracking?.track("job_ad_display_next", {
      ...properties,
      content_type: "job_offers",
      context: "job_search",
      is_public: true,
      page,
      page_size: pageSize,
      school_id: careerCenterId,
      search_id: searchId,
      service: "job_ads_fo",
      student_profile_id: userId || "",
    })

  const trackJobAdsSearch = (properties?: JobAdsSearchEventProperties): void => {
    const autocompleteSearch = properties?.autocompleteSearch
    const algoliaSearchId = properties?.algoliaSearchId

    if (!algoliaSearchId) {
      return
    }

    tracking?.track("job_ad_search_next", {
      alumni_job_ads: exclusive.includes("created_by_student_or_alumni"),
      approximate_facets: !exhaustiveFacets,
      approximate_results: !exhaustiveResults,
      company_business_type: companyBusinessTypes,
      company_sectors: companySectors,
      company_with_impact: !!jobWithImpact,
      contract_duration: contractDurations,
      contracts: contractTypes,
      easy_apply: candidacyTypes.includes(JOB_ADS_CANDIDACY_TYPES.INTERNAL),
      exclusive_job_ads: exclusive.includes("reserved"),
      faculty_partner_job_ads: exclusive.includes("partner_faculty_career_center"),
      is_a_saved_search: isSavedSearch,
      keyword: userQuery,
      languages: [], // TODO - https://jobteaser.atlassian.net/browse/DUX-4248 - previously incorrectly filled anyway / needs to fill this or find another solution
      locations: location ? [location] : [],
      nb_results: properties.nbResults,
      page_number: page,
      partner_job_ads: exclusive.includes("partner_career_center"),
      position_categories: positionCategories,
      ...(isPublicationDateFilterEnabled && {
        publication_date: publicationDate || PUBLICATION_DATE_FILTER_OPTIONS[0],
      }),
      radius: getRadiusDefaultValue(radius, location),
      remote_types: remoteTypes,
      search_id: algoliaSearchId,
      service: "job_ads_fo",
      sort_by: sortBy,
      start_date: startDates,
      study_levels: studyLevels,
      tags,
      trackee_locale: locale,
      with_keyword_only: searchParams.toString().split("&").length === 1 && !!userQuery,
      work_experience_code: workExperiences,
      ...(autocompleteSearch && { autocomplete_search: autocompleteSearch }),
    })
  }

  const trackJobAdSave = (like: boolean, properties: JobAdSaveEventProperties): void => {
    const trackingEvent = like ? "job_ad_save" : "job_ad_dismiss_click"
    const context = properties.context || "job-ad"

    tracking?.track(trackingEvent, {
      ...properties,
      context,
      search_id: searchId,
    })
  }

  const trackJobAdClick = (properties: JobAdClickEventProperties): void => {
    const context = properties.context || "job-ad"

    tracking?.track("job_ad_click", {
      ...properties,
      context,
      job_ads_count: totalSize,
      page,
      page_size: pageSize,
      school_id: careerCenterId,
      search_id: searchId,
      service: "job_ads_fo",
    })
  }

  const trackSavedSearchClick = (savedSearchParams: SearchParams, savedSearchId: string): void => {
    tracking?.track("saved_search_click", {
      company_business_type: savedSearchParams.companyBusinessTypes,
      company_sectors: savedSearchParams.companySectors,
      contract_duration: savedSearchParams.contractDurations,
      contracts: savedSearchParams.contracts,
      easy_apply: savedSearchParams.candidacyTypes.includes(JOB_ADS_CANDIDACY_TYPES.INTERNAL),
      exclusive_job_ads: savedSearchParams.exclusive.includes("reserved"),
      faculty_partner_job_ads: savedSearchParams.exclusive.includes("partner_faculty_career_center"),
      keyword: savedSearchParams.keywords,
      languages: savedSearchParams.locales,
      locations: savedSearchParams.locations,
      module: "jobsearch",
      partner_job_ads: savedSearchParams.exclusive.includes("partner_career_center"),
      position_categories: savedSearchParams.positionCategories,
      radius: getRadiusDefaultValue(Number(savedSearchParams.radius), savedSearchParams.locations[0]),
      remote_types: savedSearchParams.remoteTypes,
      search_id: savedSearchId,
      service: "job_ads_fo",
      sort_by: sortBy,
      start_date: savedSearchParams.startDates,
      study_levels: savedSearchParams.studyLevels,
      tags: savedSearchParams.tags,
      trackee_locale: locale,
      work_experience_code: savedSearchParams.workExperienceCodes,
    })
  }

  const trackSavedSearchCreated = (savedSearchParams: SearchParams, { hasAlreadySavedSearch = false } = {}): void => {
    tracking?.track("saved_search_created", {
      already_saved: hasAlreadySavedSearch,
      company_business_type: savedSearchParams.companyBusinessTypes,
      company_sectors: savedSearchParams.companySectors,
      contract_duration: savedSearchParams.contractDurations,
      contracts: savedSearchParams.contracts,
      easy_apply: savedSearchParams.candidacyTypes.includes(JOB_ADS_CANDIDACY_TYPES.INTERNAL),
      exclusive_job_ads: savedSearchParams.exclusive.includes("reserved"),
      faculty_partner_job_ads: savedSearchParams.exclusive.includes("partner_faculty_career_center"),
      keyword: savedSearchParams.keywords,
      languages: savedSearchParams.locales,
      locations: savedSearchParams.locations,
      partner_job_ads: savedSearchParams.exclusive.includes("partner_career_center"),
      position_categories: savedSearchParams.positionCategories,
      radius: getRadiusDefaultValue(Number(savedSearchParams.radius), savedSearchParams.locations[0]),
      remote_types: savedSearchParams.remoteTypes,
      service: "job_ads_fo",
      start_date: savedSearchParams.startDates,
      study_levels: savedSearchParams.studyLevels,
      tags: savedSearchParams.tags,
      trackee_locale: locale,
      work_experience_code: savedSearchParams.workExperienceCodes,
    })
  }

  const trackSavedSearchDeleted = (savedSearchParams: SearchParams, module: SavedSearchModuleProperties): void => {
    tracking?.track("saved_search_deleted", {
      company_business_type: savedSearchParams.companyBusinessTypes,
      company_sectors: savedSearchParams.companySectors,
      contract_duration: savedSearchParams.contractDurations,
      contracts: savedSearchParams.contracts,
      easy_apply: savedSearchParams.candidacyTypes.includes(JOB_ADS_CANDIDACY_TYPES.INTERNAL),
      exclusive_job_ads: savedSearchParams.exclusive.includes("reserved"),
      faculty_partner_job_ads: savedSearchParams.exclusive.includes("partner_faculty_career_center"),
      keyword: savedSearchParams.keywords,
      languages: savedSearchParams.locales,
      locations: savedSearchParams.locations,
      module,
      partner_job_ads: savedSearchParams.exclusive.includes("partner_career_center"),
      position_categories: savedSearchParams.positionCategories,
      radius: getRadiusDefaultValue(Number(savedSearchParams.radius), savedSearchParams.locations[0]),
      remote_types: savedSearchParams.remoteTypes,
      service: "job_ads_fo",
      start_date: savedSearchParams.startDates,
      study_levels: savedSearchParams.studyLevels,
      tags: savedSearchParams.tags,
      trackee_locale: locale,
      work_experience_code: savedSearchParams.workExperienceCodes,
    })
  }

  /*
   * Emitted when an user submit the saved search creation/update/edit form.
   */
  const trackSavedSearchSubmitForm = (step: SavedSearchFormStep): void => {
    tracking?.track("saved_search_form_submit_click", {
      service: "job_ads_fo",
      step,
      trackee_locale: locale,
    })
  }

  /**
   * Emmited when user opt-in/opt-out to the job alert.
   */
  const trackSavedSearchJobAlertOptInClick = (isOptIn: boolean, module: SavedSearchModuleProperties): void => {
    tracking?.track("job_alert_opt_in_search", {
      module,
      opt_in: isOptIn,
      service: "job_ads_fo",
      trackee_locale: locale,
    })
  }

  return {
    setRefererParams,
    trackJobAdClick,
    trackJobAdDisplayNext,
    trackJobAdSave,
    trackJobAdsSearch,
    trackSavedSearchClick,
    trackSavedSearchCreated,
    trackSavedSearchDeleted,
    trackSavedSearchJobAlertOptInClick,
    trackSavedSearchSubmitForm,
  }
}
